import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Protection } from "@sportal/api";
import _ from "lodash";

import {
  CommonModalHeader,
  ModalDialog,
} from "../../../../../components/modal";
import { getProfileProtection } from "../../../../../store/profiles/profiles.selectors";
import { SBCategory } from "../../../../../store/categories/categories.types";
import { Button } from "../../../../../components/button/Button";
import { useIntlTranslate } from "../../../../../hooks";

import {
  ChangeProtectionCategoriesFn,
  ProtectionCategoryDetails,
  ProtectionCategoryWithAction,
} from "../protectionEditor/protectionEditor.types";
import { ProtectionStatus } from "../protectionEditor/protectionEditor.constants";
import { CategoriesList } from "../protectionEditor/CategoriesList";
import { ProtectionSearch } from "../protectionSearch/ProtectionSearch";
import {
  getAllowed,
  getBlocked,
  getDetailedCategories,
} from "../webFilters.helpers";
import { CategoriesByStatus } from "../webFilters.types";
import { useFilterCategories } from "../hooks";
import { ProtectionEditorDialogActions } from "./ProtectionEditorDialogActions";

import "./ProtectionEditorDialog.scss";

const setStatus = (
  categories: SBCategory[],
  isBlocked: boolean
): CategoryWithStatus[] =>
  _.map(categories, (category) => ({
    ...category,
    isBlocked,
  }));

const setBlocked = (categories: SBCategory[]) => setStatus(categories, true);
const setAllowed = (categories: SBCategory[]) => setStatus(categories, false);

const getNames = (categories: ProtectionCategoryDetails[]): string[] =>
  _.map(categories, (category) => category.name);

const getCategoriesWithAction = (
  categories: CategoryWithStatus[],
  changeAction: (names: string[]) => void
): ProtectionCategoryWithAction[] =>
  _.map(categories, (category) => ({
    ...category,
    id: category.name,
    changeAction: changeAction,
    icon: category.isBlocked ? "fa-ban color-danger" : "fa-check color-success",
  }));

const useDetailedCategories = (
  categories: CategoryWithStatus[],
  toggleStatus: (names: string[]) => void,
  blockedByWebFilter: string[],
  profileId: string
): ProtectionCategoryDetails[] => {
  const translate = useIntlTranslate();

  const categoriesWithAction = getCategoriesWithAction(
    categories,
    toggleStatus
  );

  const protection: Protection = useSelector(getProfileProtection(profileId));

  return getDetailedCategories(
    categoriesWithAction,
    blockedByWebFilter,
    protection,
    translate
  );
};

interface CategoryWithStatus extends SBCategory {
  isBlocked: boolean;
}

interface ProtectionEditorDialogProps {
  categories: CategoriesByStatus;
  profileId: string;
  blockedByWebFilter: string[];
  modal: any;
}

export const ProtectionEditorDialog = ({
  categories: categoriesByStatus,
  profileId,
  blockedByWebFilter,
  modal,
}: ProtectionEditorDialogProps) => {
  const [categories, setCategories] = useState([
    ...setBlocked(categoriesByStatus.blocked),
    ...setAllowed(categoriesByStatus.allowed),
  ]);

  const toggleStatus = useCallback(
    (categoryNames: string[]) => {
      setCategories(
        categories.map((category) =>
          _.includes(categoryNames, category.name)
            ? { ...category, isBlocked: !category.isBlocked }
            : category
        )
      );
    },
    [categories, setCategories]
  );

  const detailedCategories = useDetailedCategories(
    categories,
    toggleStatus,
    blockedByWebFilter,
    profileId
  );

  const { input, setInput, filteredCategories } =
    useFilterCategories(detailedCategories);

  const handleToggleAllStatus = (status: ProtectionStatus) => {
    const isBlocked = status !== ProtectionStatus.Blocked;
    const categoriesToChange = _.filter(filteredCategories, { isBlocked });

    toggleStatus(getNames(categoriesToChange));
  };

  const handleToggleStatus = (
    action: ChangeProtectionCategoriesFn,
    categoryNames: string[]
  ) => action(categoryNames);

  const handleSubmit = (): void => {
    const blockedCategories = _.filter(detailedCategories, { isBlocked: true });
    modal.resolve(getNames(blockedCategories));
  };

  return (
    <ModalDialog
      className="protection-editor-modal"
      rejectHandler={() => modal.reject()}
    >
      <CommonModalHeader>
        <FormattedMessage id="protection_level_categories" />
      </CommonModalHeader>

      <ModalDialog.Body>
        <ProtectionSearch
          inputId="categories-search"
          onChange={setInput}
          filterValue={input}
        />
        <div className="categories-list-wrapper">
          <CategoriesList
            list={filteredCategories}
            onClick={handleToggleStatus}
          />
        </div>
      </ModalDialog.Body>

      <ModalDialog.Footer>
        <ProtectionEditorDialogActions
          onToggleAll={(status) => handleToggleAllStatus(status)}
          areAllBlocked={_.isEmpty(filteredCategories.filter(getAllowed))}
          areAllAllowed={_.isEmpty(filteredCategories.filter(getBlocked))}
        />

        <ModalDialog.Control>
          <Button color="primary" onClick={handleSubmit} size="large">
            <FormattedMessage id="ok" />
          </Button>
        </ModalDialog.Control>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};
